import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  AlertTriangle,
  AtSign,
  Bell,
  BellOff,
  GraduationCap,
  Info,
  Loader2,
  Mail,
  MessageCircle,
  Plus,
  ShieldQuestion,
  Sparkles,
  Trash2,
  Users,
  Webhook,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type NotificationChannel,
  type NotificationEventDescriptor,
  type NotificationRecipientInput,
  type NotificationRecipientKind,
  type NotificationTrigger,
  type NotificationTriggerCreateInput,
  type NotificationTriggerUpdateInput,
  type UserRole,
} from "@eduviaos/shared";
import {
  useCreateNotificationTrigger,
  useNotificationEventCatalogue,
  useUpdateNotificationTrigger,
  type UseUpdateNotificationTriggerVars,
} from "@/hooks/useNotificationTriggers";
import { useDesignations } from "@/hooks/useDesignations";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { draftRow, useAIDraftGenerator, type NotificationTriggerDraft } from "@/lib/aiDraft";

/**
 * Phase F0b — NotificationTrigger editor sheet.
 *
 * Used in two modes:
 *   - mode="create" → renders a blank sheet, calls POST on save.
 *   - mode="edit"   → renders prefilled from `existing`, calls PATCH.
 *
 * Surface contract (matches the DesignationEditorSheet structure for
 * consistency across the IA Settings UI):
 *   1. Identity                  → name, description.
 *   2. Event                     → catalogued dropdown + free-form
 *      sub-namespace input. Unknown events fall back to a soft warning
 *      banner — never a hard block (see event_catalogue.py rationale).
 *   3. Channels                  → checkbox grid. EMAIL + IN_APP are
 *      live; SMS + WEBHOOK render disabled with a "reserved" tooltip
 *      so admins can preview the road-map.
 *   4. Recipients                → a list editor where each row picks a
 *      kind from a dropdown and exposes the kind-specific config inputs
 *      (role for STAFF_ROLE, designation slug for STAFF_DESIGNATION,
 *      emails chip-input for CUSTOM_EMAILS).
 *   5. Template + advanced       → template slug input + a collapsible
 *      "Conditions (advanced)" section with a JSON textarea. Empty
 *      conditions object is the default; non-empty values are validated
 *      as JSON before save.
 *
 * Save is gated on:
 *   - non-empty name (BE rejects empty anyway, but we short-circuit)
 *   - non-empty event identifier
 *   - at least one channel
 *   - at least one recipient with a kind selected
 *   - if recipient kind requires `config`, the config field is non-empty
 *
 * The BE rejects empty channels/recipients with a 422 so we keep the
 * sheet open and surface the inline error instead of round-tripping.
 *
 * Iron Rule #2 — every reserved channel renders end-to-end with a
 * tooltip explaining when it lands. The admin never has to wonder
 * "why doesn't SMS show up?"
 */

const CHANNEL_OPTIONS: ReadonlyArray<{
  value: NotificationChannel;
  label: string;
  helper: string;
  icon: React.ComponentType<{ className?: string }>;
  reserved?: string;
}> = [
  {
    value: "email",
    label: "Email",
    helper: "Sends the linked email template.",
    icon: Mail,
  },
  {
    value: "in_app",
    label: "In-app notification",
    helper: "Shows in your team's bell menu and as a banner on the applicant's portal.",
    icon: Bell,
  },
  {
    value: "sms",
    label: "SMS",
    helper:
      "Reserved — SMS notifications using the same balance-managed pipeline as login OTPs. Coming soon.",
    icon: MessageCircle,
    reserved: "Coming soon",
  },
  {
    value: "webhook",
    label: "Webhook",
    helper:
      "Reserved — fires a signed POST to a URL you configure. Coming soon alongside SSO and payment-gateway integrations.",
    icon: Webhook,
    reserved: "Coming soon",
  },
];

interface RecipientKindOption {
  value: NotificationRecipientKind;
  label: string;
  helper: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Renders the inline config sub-form for this kind. */
  configKind: "none" | "role" | "designation_slug" | "custom_emails";
}

const RECIPIENT_KIND_OPTIONS: ReadonlyArray<RecipientKindOption> = [
  {
    value: "applicant",
    label: "Applicant",
    helper:
      "Resolves to the enquiry's student email (and SMS phone for SMS channels). Skipped silently when missing — common for early-stage enquiries.",
    icon: GraduationCap,
    configKind: "none",
  },
  {
    value: "parent",
    label: "Parent",
    helper: "Resolves to the enquiry's parent email/phone. Skipped silently when missing.",
    icon: Users,
    configKind: "none",
  },
  {
    value: "assigned_owner",
    label: "Assigned owner",
    helper:
      "The counsellor / staff user the enquiry is assigned to. Skipped silently for unassigned enquiries.",
    icon: ShieldQuestion,
    configKind: "none",
  },
  {
    value: "collaborators",
    label: "All collaborators",
    helper:
      "Every collaborator on the enquiry. Fans out to multiple deliveries when the enquiry has more than one helper.",
    icon: Users,
    configKind: "none",
  },
  {
    value: "staff_role",
    label: "Staff by role",
    helper:
      "Every active user with the chosen platform role (admin / counsellor / etc.). Useful for back-office alerts.",
    icon: Users,
    configKind: "role",
  },
  {
    value: "staff_designation",
    label: "Staff by designation",
    helper:
      "Every active user anchored to a specific designation (e.g. 'front-office', 'principal').",
    icon: Users,
    configKind: "designation_slug",
  },
  {
    value: "custom_emails",
    label: "Custom email list",
    helper:
      "Literal list of email addresses. Useful for shared inboxes (admissions@school.example) without creating user accounts.",
    icon: AtSign,
    configKind: "custom_emails",
  },
];

const STAFF_ROLE_OPTIONS: ReadonlyArray<{ value: UserRole; label: string }> = [
  { value: "admin", label: "Admin" },
  { value: "teacher", label: "Teacher" },
  { value: "staff", label: "Staff" },
  { value: "owner", label: "Owner" },
];

export interface NotificationTriggerEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied, the sheet renders in edit mode and the save call is a PATCH. */
  existing?: NotificationTrigger | null;
  /** Optional callback fired AFTER the save succeeds. */
  onSaved?: (saved: NotificationTrigger, action: "create" | "update") => void;
}

interface DraftRecipient extends NotificationRecipientInput {
  /** Stable id so the React list key is independent of array index. */
  uid: string;
}

interface DraftState {
  name: string;
  description: string;
  /** Catalogued event base (e.g. `stage_advanced`). Empty when the
   * admin authored a free-form event below. */
  baseEvent: string;
  /** Optional sub-namespace appended after `:` (e.g. `application_submitted`).
   * Free-form when `baseEvent` is empty (the admin can author against
   * an event we don't yet know). */
  subEvent: string;
  template_slug: string;
  channels: Set<NotificationChannel>;
  recipients: DraftRecipient[];
  enabled: boolean;
  conditionsRaw: string;
}

const FREEFORM_BASE_EVENT_VALUE = "__freeform__" as const;

function makeRecipientUid(): string {
  return `r-${Math.random().toString(36).slice(2, 10)}`;
}

function splitEvent(event: string): { base: string; sub: string } {
  if (!event) return { base: "", sub: "" };
  const idx = event.indexOf(":");
  if (idx === -1) return { base: event, sub: "" };
  return { base: event.slice(0, idx), sub: event.slice(idx + 1) };
}

function joinEvent(base: string, sub: string): string {
  const baseTrim = base.trim();
  const subTrim = sub.trim();
  if (!baseTrim) return subTrim;
  if (!subTrim) return baseTrim;
  return `${baseTrim}:${subTrim}`;
}

function recipientFromExisting(r: NotificationRecipientInput): DraftRecipient {
  return { kind: r.kind, config: { ...r.config }, uid: makeRecipientUid() };
}

function draftFromExisting(existing: NotificationTrigger | null | undefined): DraftState {
  const event = existing?.event ?? "";
  const split = splitEvent(event);
  return {
    name: existing?.name ?? "",
    description: existing?.description ?? "",
    baseEvent: split.base,
    subEvent: split.sub,
    template_slug: existing?.template_slug ?? "",
    channels: new Set<NotificationChannel>(existing?.channels ?? ["email"]),
    recipients:
      existing?.recipients && existing.recipients.length > 0
        ? existing.recipients.map(recipientFromExisting)
        : [
            {
              kind: "applicant" as NotificationRecipientKind,
              config: {},
              uid: makeRecipientUid(),
            },
          ],
    enabled: existing?.enabled ?? true,
    conditionsRaw:
      existing?.conditions && Object.keys(existing.conditions).length > 0
        ? JSON.stringify(existing.conditions, null, 2)
        : "",
  };
}

interface SuggestedMessage {
  subject: string | null;
  body: string | null;
}

function recipientSummary(recipient: NotificationTriggerDraft["recipients"][number]): string {
  const label =
    RECIPIENT_KIND_OPTIONS.find((o) => o.value === recipient.kind)?.label ?? recipient.kind;
  const detail = Object.values(recipient.config)
    .map((v) => (Array.isArray(v) ? v.join(", ") : v))
    .filter(Boolean)
    .join(", ");
  return detail ? `${label} (${detail})` : label;
}

function describeTriggerDraft(draft: NotificationTriggerDraft) {
  return [
    ...draftRow("Name", draft.name),
    ...draftRow("Event", draft.event),
    ...draftRow("Channels", draft.channels.join(", ")),
    ...draftRow("Recipients", draft.recipients.map(recipientSummary).join("; ")),
    ...draftRow("Template slug", draft.template_slug),
    ...draftRow("Message subject", draft.message_subject),
    ...draftRow("Message", draft.message_body),
  ];
}

function mergeTriggerDraft(prev: DraftState, draft: NotificationTriggerDraft): DraftState {
  const split = draft.event ? splitEvent(draft.event) : null;
  return {
    ...prev,
    name: draft.name ?? prev.name,
    description: draft.description ?? prev.description,
    baseEvent: split ? split.base : prev.baseEvent,
    subEvent: split ? split.sub : prev.subEvent,
    template_slug: draft.template_slug ?? prev.template_slug,
    channels:
      draft.channels.length > 0 ? new Set(draft.channels as NotificationChannel[]) : prev.channels,
    recipients:
      draft.recipients.length > 0
        ? draft.recipients.map((r) => ({
            kind: r.kind as NotificationRecipientKind,
            config: { ...r.config },
            uid: makeRecipientUid(),
          }))
        : prev.recipients,
  };
}

function configKindFor(kind: NotificationRecipientKind): RecipientKindOption["configKind"] {
  return RECIPIENT_KIND_OPTIONS.find((o) => o.value === kind)?.configKind ?? "none";
}

function defaultConfigFor(kind: NotificationRecipientKind): Record<string, unknown> {
  switch (configKindFor(kind)) {
    case "role":
      return { role: "admin" };
    case "designation_slug":
      return { designation_slug: "" };
    case "custom_emails":
      return { emails: [] };
    default:
      return {};
  }
}

export function NotificationTriggerEditorSheet({
  open,
  onOpenChange,
  existing,
  onSaved,
}: NotificationTriggerEditorSheetProps) {
  const isEdit = Boolean(existing);
  const reduceMotion = useReducedMotion();
  const { ct } = useTerms();
  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [error, setError] = React.useState<string | null>(null);
  const [conditionsError, setConditionsError] = React.useState<string | null>(null);
  const [showConditions, setShowConditions] = React.useState(false);
  const [suggestedMessage, setSuggestedMessage] = React.useState<SuggestedMessage | null>(null);
  const generateTriggerDraft = useAIDraftGenerator("notification_trigger");
  const applyTriggerDraft = React.useCallback((next: NotificationTriggerDraft) => {
    setDraft((prev) => mergeTriggerDraft(prev, next));
    setSuggestedMessage(
      next.message_subject || next.message_body
        ? { subject: next.message_subject, body: next.message_body }
        : null,
    );
    setError(null);
  }, []);

  const createMutation = useCreateNotificationTrigger();
  const updateMutation = useUpdateNotificationTrigger();
  const catalogueQuery = useNotificationEventCatalogue();
  const isPending = createMutation.isPending || updateMutation.isPending;

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setSuggestedMessage(null);
      setError(null);
      setConditionsError(null);
      setShowConditions(
        Boolean(existing?.conditions && Object.keys(existing.conditions).length > 0),
      );
    }
  }, [open, existing]);

  // Stabilise the catalogue array across renders so the dependent
  // useMemos don't recompute every render (which would also re-trigger
  // their downstream selectors). Without this, the `catalogue` literal
  // is a fresh array reference each render even when the underlying
  // data hasn't changed.
  const catalogue = React.useMemo(() => catalogueQuery.data?.items ?? [], [catalogueQuery.data]);
  const knownEventNames = React.useMemo(() => new Set(catalogue.map((e) => e.event)), [catalogue]);
  const baseEventIsKnown =
    !draft.baseEvent ||
    knownEventNames.has(draft.baseEvent) ||
    draft.baseEvent === FREEFORM_BASE_EVENT_VALUE;

  const isFreeform =
    draft.baseEvent === FREEFORM_BASE_EVENT_VALUE ||
    (!baseEventIsKnown && Boolean(draft.baseEvent));

  const composedEvent = React.useMemo(() => {
    if (draft.baseEvent === FREEFORM_BASE_EVENT_VALUE) {
      // In freeform mode, the sub-event input becomes the full event.
      return draft.subEvent.trim();
    }
    return joinEvent(draft.baseEvent, draft.subEvent);
  }, [draft.baseEvent, draft.subEvent]);

  const selectedDescriptor: NotificationEventDescriptor | null = React.useMemo(
    () => catalogue.find((e) => e.event === draft.baseEvent) ?? null,
    [catalogue, draft.baseEvent],
  );

  // ── Channel + recipient mutators ────────────────────────────────────
  const toggleChannel = (channel: NotificationChannel, checked: boolean) => {
    setDraft((prev) => {
      const next = new Set(prev.channels);
      if (checked) next.add(channel);
      else next.delete(channel);
      return { ...prev, channels: next };
    });
  };

  const addRecipient = () => {
    setDraft((prev) => ({
      ...prev,
      recipients: [...prev.recipients, { kind: "applicant", config: {}, uid: makeRecipientUid() }],
    }));
  };

  const removeRecipient = (uid: string) => {
    setDraft((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((r) => r.uid !== uid),
    }));
  };

  const updateRecipientKind = (uid: string, kind: NotificationRecipientKind) => {
    setDraft((prev) => ({
      ...prev,
      recipients: prev.recipients.map((r) =>
        r.uid === uid ? { ...r, kind, config: defaultConfigFor(kind) } : r,
      ),
    }));
  };

  const updateRecipientConfig = (uid: string, patch: Record<string, unknown>) => {
    setDraft((prev) => ({
      ...prev,
      recipients: prev.recipients.map((r) =>
        r.uid === uid ? { ...r, config: { ...r.config, ...patch } } : r,
      ),
    }));
  };

  // ── Save ────────────────────────────────────────────────────────────
  const onSave = async () => {
    setError(null);
    setConditionsError(null);

    const trimmedName = draft.name.trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }
    if (trimmedName.length > 120) {
      setError("Name must be at most 120 characters.");
      return;
    }
    if (draft.description.length > 500) {
      setError("Description must be at most 500 characters.");
      return;
    }
    if (!composedEvent) {
      setError("Pick a catalogued event or author a freeform event identifier.");
      return;
    }
    if (composedEvent.length > 120) {
      setError("Event identifier must be at most 120 characters.");
      return;
    }
    const trimmedTemplate = draft.template_slug.trim();
    if (!trimmedTemplate) {
      setError("Template slug is required (the BE rejects empty slugs).");
      return;
    }
    if (draft.channels.size === 0) {
      setError("Pick at least one channel — a trigger that fires nowhere is a footgun.");
      return;
    }
    if (draft.recipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }

    // Validate per-recipient config (e.g. custom_emails has at least one address).
    for (const r of draft.recipients) {
      const ck = configKindFor(r.kind);
      if (ck === "designation_slug") {
        const slug = String(r.config.designation_slug ?? "").trim();
        if (!slug) {
          setError(`Recipient '${r.kind}' needs a designation slug — pick the one to fan out to.`);
          return;
        }
      }
      if (ck === "role") {
        const role = String(r.config.role ?? "").trim();
        if (!role) {
          setError(`Recipient '${r.kind}' needs a role.`);
          return;
        }
      }
      if (ck === "custom_emails") {
        const emailsRaw = r.config.emails;
        const emails = Array.isArray(emailsRaw)
          ? emailsRaw.filter((e) => typeof e === "string")
          : [];
        if (emails.length === 0) {
          setError(`Recipient '${r.kind}' needs at least one email address.`);
          return;
        }
      }
    }

    // Validate conditions JSON (when provided).
    let parsedConditions: Record<string, unknown> = {};
    if (draft.conditionsRaw.trim()) {
      try {
        const parsed = JSON.parse(draft.conditionsRaw) as unknown;
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
          throw new Error("Conditions must be a JSON object.");
        }
        parsedConditions = parsed as Record<string, unknown>;
      } catch (e) {
        setConditionsError(
          e instanceof Error ? e.message : "Conditions must be a valid JSON object.",
        );
        return;
      }
    }

    const payload: NotificationTriggerCreateInput = {
      name: trimmedName,
      description: draft.description.trim() ? draft.description.trim() : null,
      event: composedEvent,
      template_slug: trimmedTemplate,
      channels: Array.from(draft.channels),
      recipients: draft.recipients.map((r) => ({ kind: r.kind, config: r.config })),
      enabled: draft.enabled,
      conditions: parsedConditions,
    };

    try {
      if (isEdit && existing) {
        const updateBody: NotificationTriggerUpdateInput = payload;
        const vars: UseUpdateNotificationTriggerVars = { id: existing.id, body: updateBody };
        const saved = await updateMutation.mutateAsync(vars);
        onSaved?.(saved, "update");
      } else {
        const saved = await createMutation.mutateAsync(payload);
        onSaved?.(saved, "create");
      }
      onOpenChange(false);
    } catch (raw) {
      if (raw instanceof ApiError) {
        if (raw.code === "CONFLICT") {
          setError("A trigger with this name already exists. Pick a different name.");
        } else if (raw.code === "VALIDATION_ERROR") {
          setError(
            raw.message ||
              "The BE rejected the trigger — check channels, recipients, and template.",
          );
        } else {
          setError(raw.message);
        }
      } else if (raw instanceof Error && raw.message) {
        setError(raw.message);
      } else {
        setError("Could not save the trigger. Please try again.");
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-border/60 border-b p-6">
          <SheetTitle>
            {isEdit ? `Edit ${existing?.name}` : "Author a notification trigger"}
          </SheetTitle>
          <SheetDescription>
            Triggers map an event (e.g. an enquiry advancing to "application submitted") to a
            template + recipients. Events are processed asynchronously — saves take effect within a
            few seconds.
          </SheetDescription>
          {isEdit ? null : (
            <div className="pt-2">
              <AIDraftButton<NotificationTriggerDraft>
                title="Draft a notification"
                placeholder="e.g. Email the centre admin and the learner when an enquiry moves to application submitted"
                generate={generateTriggerDraft}
                describe={describeTriggerDraft}
                onApply={applyTriggerDraft}
                disabled={isPending}
                data-testid="trigger-ai-draft"
              />
            </div>
          )}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {/* 1. Identity */}
          <section className="mb-8 space-y-4">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              1. Identity
            </h3>
            <div className="space-y-2">
              <Label htmlFor="trigger-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="trigger-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Notify counsellor when enquiry advances to assessment"
                maxLength={120}
                autoFocus={!isEdit}
                data-testid="trigger-name"
              />
              <p className="text-muted-foreground text-xs">
                Visible on the triggers list and in the audit log. Up to 120 characters.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trigger-description">
                Description <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="trigger-description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="What this trigger is for. Helps colleagues understand intent before they touch it."
                maxLength={500}
                rows={2}
                data-testid="trigger-description"
              />
            </div>
          </section>

          {/* 2. Event */}
          <section className="mb-8 space-y-4">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              2. Event
            </h3>
            <div className="space-y-2">
              <Label htmlFor="trigger-event-base">
                Catalogued event <span className="text-destructive">*</span>
              </Label>
              <select
                id="trigger-event-base"
                value={draft.baseEvent}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    baseEvent: e.target.value,
                    subEvent: e.target.value === FREEFORM_BASE_EVENT_VALUE ? draft.subEvent : "",
                  })
                }
                className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                data-testid="trigger-event-base"
              >
                <option value="">— Pick an event —</option>
                {catalogue.map((e) => (
                  <option key={e.event} value={e.event}>
                    {e.label} ({e.event})
                  </option>
                ))}
                <option value={FREEFORM_BASE_EVENT_VALUE}>
                  Custom event identifier (advanced)
                </option>
              </select>
              {selectedDescriptor ? (
                <div className="border-border/60 bg-muted/30 mt-2 space-y-2 rounded-md border px-3 py-2 text-xs">
                  <p className="text-foreground/90 leading-relaxed">
                    {selectedDescriptor.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
                      Payload keys:
                    </span>
                    {selectedDescriptor.sample_payload_keys.map((k) => (
                      <Badge
                        key={k}
                        variant="outline"
                        className="font-mono text-[10.5px] font-normal"
                      >
                        {k}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {isFreeform || draft.baseEvent ? (
              <div className="space-y-2">
                <Label htmlFor="trigger-event-sub">
                  {isFreeform ? "Custom event identifier" : "Sub-namespace (optional)"}
                  {isFreeform ? <span className="text-destructive"> *</span> : null}
                </Label>
                <Input
                  id="trigger-event-sub"
                  value={draft.subEvent}
                  onChange={(e) => setDraft({ ...draft, subEvent: e.target.value })}
                  placeholder={
                    isFreeform
                      ? "e.g. vertical:school:custom_event"
                      : "e.g. application_submitted (omit to match all)"
                  }
                  maxLength={120}
                  data-testid="trigger-event-sub"
                />
                <p className="text-muted-foreground text-xs">
                  {isFreeform
                    ? "Free-form event identifier. The BE accepts unknown events but flags them with a soft warning until they're wired into the dispatcher."
                    : "Sub-namespace narrows the trigger to a specific transition. Composed event: "}
                  {!isFreeform && composedEvent ? (
                    <code className="text-foreground bg-muted ml-1 rounded px-1.5 py-0.5 text-[11px]">
                      {composedEvent}
                    </code>
                  ) : null}
                </p>
              </div>
            ) : null}

            {composedEvent && !knownEventNames.has(composedEvent.split(":")[0]!) ? (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
              >
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">
                  This event isn't yet in the platform catalogue. The trigger will save
                  (forward-compat events are allowed), but no notifications will fire until
                  something dispatches{" "}
                  <code className="rounded bg-amber-500/10 px-1 py-0.5 font-mono text-[10.5px]">
                    {composedEvent.split(":")[0]}
                  </code>
                  .
                </span>
              </div>
            ) : null}
          </section>

          {/* 3. Channels */}
          <section className="mb-8 space-y-4">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              3. Channels
            </h3>
            <p className="text-muted-foreground text-xs">
              Pick where this notification ends up. At least one channel is required.
            </p>
            <TooltipProvider delayDuration={150}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {CHANNEL_OPTIONS.filter((opt) => !opt.reserved).map((opt) => {
                  const Icon = opt.icon;
                  const isReserved = Boolean(opt.reserved);
                  const isChecked = draft.channels.has(opt.value);
                  const row = (
                    <Label
                      key={opt.value}
                      htmlFor={`channel-${opt.value}`}
                      className={cn(
                        "border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                        isChecked && !isReserved
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/40",
                        isReserved && "cursor-not-allowed opacity-60 hover:bg-transparent",
                      )}
                    >
                      <Checkbox
                        id={`channel-${opt.value}`}
                        checked={isChecked}
                        disabled={isReserved}
                        onCheckedChange={(c) => toggleChannel(opt.value, Boolean(c))}
                        className="mt-0.5"
                      />
                      <Icon
                        aria-hidden="true"
                        className="text-muted-foreground mt-0.5 size-4 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground text-sm font-medium">{ct(opt.label)}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                          {ct(opt.helper)}
                        </p>
                      </div>
                    </Label>
                  );
                  if (isReserved) {
                    return (
                      <Tooltip key={opt.value}>
                        <TooltipTrigger asChild>{row}</TooltipTrigger>
                        <TooltipContent side="top">{opt.reserved}</TooltipContent>
                      </Tooltip>
                    );
                  }
                  return row;
                })}
              </div>
            </TooltipProvider>
          </section>

          {/* 4. Recipients */}
          <section className="mb-8 space-y-4">
            <div className="flex items-end justify-between gap-2">
              <div>
                <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                  4. Recipients
                </h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Who actually receives the notification. Each row is resolved independently at
                  dispatch time — empty recipients are silently skipped (with a log).
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addRecipient}
                data-testid="add-recipient"
              >
                <Plus aria-hidden="true" className="size-3.5" />
                Add recipient
              </Button>
            </div>

            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {draft.recipients.map((r, idx) => (
                  <motion.div
                    key={r.uid}
                    initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 8 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <RecipientRow
                      index={idx}
                      recipient={r}
                      onKindChange={(kind) => updateRecipientKind(r.uid, kind)}
                      onConfigChange={(patch) => updateRecipientConfig(r.uid, patch)}
                      onRemove={() => removeRecipient(r.uid)}
                      canRemove={draft.recipients.length > 1}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </section>

          {/* 5. Template + advanced */}
          <section className="space-y-4">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              5. Template &amp; advanced
            </h3>
            <div className="space-y-2">
              <Label htmlFor="trigger-template">
                Template slug <span className="text-destructive">*</span>
              </Label>
              <Input
                id="trigger-template"
                value={draft.template_slug}
                onChange={(e) => setDraft({ ...draft, template_slug: e.target.value })}
                placeholder="e.g. enquiry-advanced-application-submitted"
                maxLength={120}
                data-testid="trigger-template"
              />
              <p className="text-muted-foreground text-xs">
                Foreign key into the EmailTemplate registry (when the email channel is on) or a
                stable in-app slug. Typos surface as a delivery failure — pick from the email
                templates page.
              </p>
            </div>

            {suggestedMessage ? (
              <div
                className="border-border/60 bg-muted/40 space-y-1 rounded-lg border p-3 text-sm"
                data-testid="trigger-suggested-message"
              >
                <p className="text-muted-foreground text-xs font-medium">
                  Suggested message — use it when you write this template
                </p>
                {suggestedMessage.subject ? (
                  <p className="font-medium">{suggestedMessage.subject}</p>
                ) : null}
                {suggestedMessage.body ? (
                  <p className="whitespace-pre-line">{suggestedMessage.body}</p>
                ) : null}
              </div>
            ) : null}

            <Label
              htmlFor="trigger-enabled"
              className="border-border/60 hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors"
            >
              <Checkbox
                id="trigger-enabled"
                checked={draft.enabled}
                onCheckedChange={(c) => setDraft({ ...draft, enabled: Boolean(c) })}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-medium">
                  {draft.enabled ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Bell aria-hidden="true" className="size-3.5" />
                      Enabled — fires on matching events
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <BellOff aria-hidden="true" className="size-3.5" />
                      Disabled — config kept, no dispatches
                    </span>
                  )}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  Soft-disable so the audit trail captures "we paused this trigger" without losing
                  the recipients/template wiring.
                </p>
              </div>
            </Label>

            <button
              type="button"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs"
              onClick={() => setShowConditions((s) => !s)}
              data-testid="toggle-conditions"
            >
              <Sparkles aria-hidden="true" className="size-3" />
              {showConditions ? "Hide" : "Show"} advanced conditions
            </button>
            {showConditions ? (
              <div className="space-y-2">
                <Label htmlFor="trigger-conditions">Conditions (JSON object)</Label>
                <Textarea
                  id="trigger-conditions"
                  value={draft.conditionsRaw}
                  onChange={(e) => setDraft({ ...draft, conditionsRaw: e.target.value })}
                  placeholder={'{ "stage_key": "application_submitted" }'}
                  rows={5}
                  className="font-mono text-xs"
                  data-testid="trigger-conditions"
                />
                <p className="text-muted-foreground text-xs">
                  Optional payload-filter applied at dispatch time. F0 supports flat key=value
                  matches against the event payload (e.g. only fire when{" "}
                  <code className="bg-muted rounded px-1 py-0.5">stage_key</code> matches the value
                  here). Leave empty to match all event-payloads for the chosen event.
                </p>
                {conditionsError ? (
                  <p className="text-destructive text-xs">{conditionsError}</p>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>

        <div className="border-border/60 flex flex-col gap-3 border-t p-6">
          {error ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {error}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <SheetClose asChild>
              <Button variant="outline" disabled={isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button onClick={() => void onSave()} disabled={isPending} data-testid="save-trigger">
              {isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Saving…
                </>
              ) : isEdit ? (
                "Save changes"
              ) : (
                "Create trigger"
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface RecipientRowProps {
  index: number;
  recipient: NotificationRecipientInput;
  onKindChange: (kind: NotificationRecipientKind) => void;
  onConfigChange: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function RecipientRow({
  index,
  recipient,
  onKindChange,
  onConfigChange,
  onRemove,
  canRemove,
}: RecipientRowProps) {
  const { ct } = useTerms();
  const opt = RECIPIENT_KIND_OPTIONS.find((o) => o.value === recipient.kind);
  const Icon = opt?.icon ?? Users;
  return (
    <div className="border-border/60 rounded-lg border p-3">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="bg-muted text-muted-foreground mt-1 grid size-7 shrink-0 place-items-center rounded-md"
        >
          <Icon className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <Label htmlFor={`recipient-kind-${index}`} className="text-xs">
                Recipient kind
              </Label>
              <select
                id={`recipient-kind-${index}`}
                value={recipient.kind}
                onChange={(e) => onKindChange(e.target.value as NotificationRecipientKind)}
                className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-9 w-full rounded-md border px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
                data-testid={`recipient-kind-${index}`}
              >
                {RECIPIENT_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {ct(o.label)}
                  </option>
                ))}
              </select>
            </div>
            {canRemove ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onRemove}
                aria-label={`Remove recipient ${index + 1}`}
                className="text-muted-foreground hover:text-destructive mt-5 shrink-0"
                data-testid={`remove-recipient-${index}`}
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
              </Button>
            ) : null}
          </div>
          {opt?.helper ? (
            <p className="text-muted-foreground flex items-start gap-1 text-xs leading-relaxed">
              <Info aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
              <span>{ct(opt.helper)}</span>
            </p>
          ) : null}
          <RecipientConfigEditor
            recipient={recipient}
            onConfigChange={onConfigChange}
            index={index}
          />
        </div>
      </div>
    </div>
  );
}

interface RecipientConfigEditorProps {
  recipient: NotificationRecipientInput;
  onConfigChange: (patch: Record<string, unknown>) => void;
  index: number;
}

function RecipientConfigEditor({ recipient, onConfigChange, index }: RecipientConfigEditorProps) {
  const { roleLabel } = useTerms();
  const ck = configKindFor(recipient.kind);
  if (ck === "none") return null;

  if (ck === "role") {
    const role = String(recipient.config.role ?? "admin");
    return (
      <div className="space-y-1">
        <Label htmlFor={`recipient-role-${index}`} className="text-xs">
          Platform role
        </Label>
        <select
          id={`recipient-role-${index}`}
          value={role}
          onChange={(e) => onConfigChange({ role: e.target.value })}
          className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-9 w-full rounded-md border px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2"
          data-testid={`recipient-role-${index}`}
        >
          {STAFF_ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {roleLabel(o.value, o.label)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (ck === "designation_slug") {
    return (
      <DesignationSlugPicker index={index} recipient={recipient} onConfigChange={onConfigChange} />
    );
  }

  if (ck === "custom_emails") {
    return (
      <CustomEmailsEditor
        emails={
          Array.isArray(recipient.config.emails)
            ? (recipient.config.emails as unknown[]).filter(
                (e): e is string => typeof e === "string",
              )
            : []
        }
        onChange={(emails) => onConfigChange({ emails })}
        index={index}
      />
    );
  }

  return null;
}

interface CustomEmailsEditorProps {
  emails: string[];
  onChange: (next: string[]) => void;
  index: number;
}

function CustomEmailsEditor({ emails, onChange, index }: CustomEmailsEditorProps) {
  const [draft, setDraft] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const tryAdd = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    // Cheap email validation — the BE does the authoritative parse.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Invalid email address.");
      return;
    }
    if (emails.includes(value)) {
      setError("Already added.");
      return;
    }
    onChange([...emails, value]);
    setDraft("");
    setError(null);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={`recipient-emails-${index}`} className="text-xs">
        Email addresses
      </Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {emails.map((e) => (
          <span
            key={e}
            className="border-border/60 bg-muted text-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
          >
            {e}
            <button
              type="button"
              onClick={() => onChange(emails.filter((x) => x !== e))}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${e}`}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        {emails.length === 0 ? (
          <span className="text-muted-foreground/60 text-[11px]">No addresses yet.</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Input
          id={`recipient-emails-${index}`}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          placeholder="admissions@school.example"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              tryAdd();
            }
          }}
          data-testid={`recipient-emails-input-${index}`}
        />
        <Button type="button" size="sm" variant="outline" onClick={tryAdd}>
          <Plus aria-hidden="true" className="size-3.5" />
          Add
        </Button>
      </div>
      {error ? <p className="text-destructive text-[11px]">{error}</p> : null}
    </div>
  );
}

interface DesignationSlugPickerProps {
  index: number;
  recipient: NotificationRecipientInput;
  onConfigChange: (patch: Record<string, unknown>) => void;
}

/**
 * Phase H0 hotfix — replace the free-text "designation slug" Input
 * with a Select bound to the configured Designations. Free text
 * silently breaks notification fan-out the moment the admin
 * mistypes the slug, with no error path back ("we sent it; nobody
 * was on the list" → silence).
 *
 * Loading / empty state strategy:
 *   - Loading → disabled placeholder so the admin doesn't think
 *     the field is broken.
 *   - Empty → inline CTA pointing to /settings/designations rather
 *     than a generic "no options" so they aren't stranded.
 *   - Stale slug (recipient.config.designation_slug doesn't match
 *     any active designation) → keep the value visible with an
 *     "Unknown · <slug>" entry so they can see what was previously
 *     saved instead of silently losing it.
 */
function DesignationSlugPicker({ index, recipient, onConfigChange }: DesignationSlugPickerProps) {
  // The BE stores `User.designation = <Designation.name>` and the
  // notification dispatcher resolves recipients by matching that
  // exact string against `STAFF_DESIGNATION.config.designation_slug`.
  // So the option value must be `Designation.name`. (Naming legacy:
  // the recipient kind was originally `designation_slug` because we
  // assumed designations would be slugged. Designation.slug never
  // landed; the BE field is just the freeform display name.)
  const currentValue = String(recipient.config.designation_slug ?? "");
  const designations = useDesignations();

  const items = designations.data?.items ?? [];
  const matched = items.find((d) => d.name === currentValue);
  const stale = currentValue !== "" && !designations.isLoading && !matched;

  return (
    <div className="space-y-1">
      <Label htmlFor={`recipient-slug-${index}`} className="text-xs">
        Designation
      </Label>
      <Select
        value={currentValue}
        onValueChange={(value) => onConfigChange({ designation_slug: value })}
        disabled={designations.isLoading || items.length === 0}
      >
        <SelectTrigger
          id={`recipient-slug-${index}`}
          data-testid={`recipient-slug-${index}`}
          className="h-9"
        >
          <SelectValue
            placeholder={
              designations.isLoading
                ? "Loading designations…"
                : items.length === 0
                  ? "No designations configured"
                  : "Pick a designation"
            }
          />
        </SelectTrigger>
        <SelectContent>
          {stale ? <SelectItem value={currentValue}>Unknown · {currentValue}</SelectItem> : null}
          {items.map((d) => (
            <SelectItem key={d.id} value={d.name}>
              {d.name}
              <span className="text-muted-foreground ml-2 text-[11px]">{d.role}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {items.length === 0 && !designations.isLoading ? (
        <p className="text-muted-foreground text-[11px]">
          No designations configured yet.{" "}
          <a
            href="/settings/designations"
            className="text-primary underline-offset-2 hover:underline"
          >
            Create one
          </a>{" "}
          before fanning out to a designation.
        </p>
      ) : (
        <p className="text-muted-foreground text-[11px]">
          Configured in{" "}
          <a
            href="/settings/designations"
            className="text-primary underline-offset-2 hover:underline"
          >
            Settings → Designations
          </a>
          .
        </p>
      )}
      {stale ? (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">
          The previously-selected designation is no longer active. Pick a new one to keep this
          recipient deliverable.
        </p>
      ) : null}
    </div>
  );
}
